import React, { FC, useMemo } from 'react';

import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Benefits from '~/modules/feature-access/components/Benefits';
import InputLimitOverflowAnimation from '~/modules/feature-access/components/InputLimitOverflowAnimation';
import { getBenefitsList } from '~/modules/feature-access/helpers';
import { InputLimitOverflowProps } from '~/modules/feature-access/modals/FeatureUnlockModal/InputLimitOverflow/types';
import { FeatureKey, FeatureModule } from '~/modules/feature-access/policy';
import projectConfig from '~/projects/config';
import { Typographies } from '~/theme/typography';

import styles from './styles.module.scss';

const InputLimitOverflow: FC<
  InputLimitOverflowProps & {
    featureKey: FeatureKey;
    featureModule: FeatureModule;
  }
> = ({
  featureKey,
  featureModule,
  increaser,
  inputCurrent,
  inputLimit,
  newLimit,
}) => {
  const { t } = useTranslation();

  const benefits = useMemo(() => {
    return getBenefitsList(t)
      .filter((el) => !(el.key === featureKey && el.module === featureModule))
      .sort(
        (a, b) =>
          Number(b.module === featureModule) -
          Number(a.module === featureModule),
      );
  }, []);

  return (
    <div className={styles.container}>
      <Typography
        variant={Typographies.TITLE_LARGE}
        component="div"
        className={styles.title}
      >
        {t('modal:feature_unlock.input_limit_overflow_title', {
          project: projectConfig.getProjectName(),
          defaultValue:
            'Upgrade to {{project}} Unlimited To Get Extra Long Input',
        })}
      </Typography>

      <div className={styles.animation}>
        <InputLimitOverflowAnimation
          increaser={increaser}
          inputCurrent={inputCurrent}
          inputLimit={inputLimit}
        />
      </div>

      <Typography
        variant={Typographies.LABEL_LARGE}
        component="div"
        className={styles.subtitle}
      >
        {t('modal:feature_unlock.input_limit_overflow_subtitle', {
          max_input_words: newLimit,
          defaultValue: 'Process up to {{max_input_words}} per request',
        })}
      </Typography>

      <Benefits benefits={benefits} />
    </div>
  );
};

export default InputLimitOverflow;
